import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle, Loader2 } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

type Method = "work_order" | "email" | "phone" | "office";

const METHODS: Array<{ value: Method; label: string; hint: string; refLabel: string | null }> = [
    {
        value: "work_order",
        label: "They sent a work order",
        hint: "The commonest one for vendor accounts — the work order IS the approval.",
        refLabel: "Work order / PO #",
    },
    {
        value: "email",
        label: "By email",
        hint: "They replied to the quote instead of clicking the link.",
        refLabel: null,
    },
    { value: "phone", label: "Over the phone", hint: "Someone called and said go ahead.", refLabel: "Who said it" },
    {
        value: "office",
        label: "In person / at the office",
        hint: "Signed a copy, or told us at the counter.",
        refLabel: "Who said it",
    },
];

interface RecordApprovalDialogProps {
    open: boolean;
    onClose: () => void;
    estimateId: string;
    estimateLabel: string;
    /** Pre-fills the reference when the quote already carries a WO number. */
    defaultReference?: string | null;
}

/**
 * Mark a quote approved when the customer said yes somewhere other than the
 * emailed link (Issue #1623).
 *
 * The office's only Approve button used to appear on a `sent` quote and record
 * nothing about how. A draft the customer had already answered could not be
 * marked approved without faking a send, so it was left live — and a later
 * work-order scan then found no job and made one.
 *
 * Recording HOW is not bookkeeping garnish: "approved by work order
 * S2604200992-3" is what lets the next person see why a quote no customer ever
 * clicked is sitting at accepted.
 */
export function RecordApprovalDialog({
    open,
    onClose,
    estimateId,
    estimateLabel,
    defaultReference,
}: RecordApprovalDialogProps) {
    const qc = useQueryClient();
    const [method, setMethod] = useState<Method>("work_order");
    const [reference, setReference] = useState(defaultReference ?? "");
    const chosen = METHODS.find((m) => m.value === method)!;

    const record = useMutation({
        mutationFn: () =>
            api.post<{ ok: boolean }>(`/api/estimates/${estimateId}/record-approval`, {
                method,
                reference: reference.trim() || null,
            }),
        onSuccess: () => {
            toast.success(`${estimateLabel} marked approved.`, {
                description: "Create the repair job next — the work order number rides along to it.",
            });
            qc.invalidateQueries({ queryKey: ["estimate", estimateId] });
            qc.invalidateQueries({ queryKey: ["estimates"] });
            onClose();
        },
        onError: (err: any) => toast.error(err?.body?.message ?? "Could not record the approval."),
    });

    return (
        <Dialog open={open} onClose={onClose} title="The customer already approved this">
            <div className="space-y-4">
                <p className="text-sm text-foreground/80">
                    Record that <span className="font-mono font-medium">{estimateLabel}</span> was approved, and how. It
                    behaves exactly as if they&apos;d clicked the link in their email.
                </p>

                <div className="space-y-1.5">
                    {METHODS.map((m) => (
                        <label
                            key={m.value}
                            className={
                                method === m.value
                                    ? "flex cursor-pointer items-start gap-2 rounded-md border-2 border-primary bg-primary/5 p-2.5"
                                    : "flex cursor-pointer items-start gap-2 rounded-md border p-2.5 hover:bg-muted/50"
                            }
                        >
                            <input
                                type="radio"
                                name="approval-method"
                                className="mt-1"
                                checked={method === m.value}
                                onChange={() => setMethod(m.value)}
                            />
                            <span className="min-w-0">
                                <span className="block text-sm font-medium">{m.label}</span>
                                <span className="block text-xs text-foreground/60">{m.hint}</span>
                            </span>
                        </label>
                    ))}
                </div>

                {chosen.refLabel && (
                    <div className="space-y-1">
                        <Label htmlFor="approval-ref" className="text-xs">
                            {chosen.refLabel} <span className="font-normal text-foreground/60">(optional)</span>
                        </Label>
                        <Input
                            id="approval-ref"
                            value={reference}
                            onChange={(e) => setReference(e.target.value)}
                            placeholder={method === "work_order" ? "S2604200992-3" : "Name"}
                        />
                        {method === "work_order" && (
                            <p className="text-[11px] text-foreground/60">
                                Stored on the quote, and carried onto the repair job — so when that work order is
                                scanned it lands on the job instead of making a second one.
                            </p>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} disabled={record.isPending}>
                        Cancel
                    </Button>
                    <Button onClick={() => record.mutate()} disabled={record.isPending}>
                        {record.isPending ? (
                            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                        ) : (
                            <CheckCircle className="mr-1 h-4 w-4" />
                        )}
                        Mark approved
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}
